// Copied from TransactionSearchResult, specialized for
// doing a follow up (void only for now) one action at a time
// rather than allowing multi-select, due to the nature of the device.
import { useContext, useEffect, useState } from 'react'
import * as React from 'react'
import _ from 'lodash'
import {
  Fieldset,
  If,
  Legend,
  Loader,
} from '@paytrace/pt-skel/Component'
import PubSub from '@paytrace/pt-skel/Event/PubSub'
import Event from '../Event'
import { BatchTransactionOpResult } from '../types'
import { hasPermission } from '../Helper/HasPermission'
import { sanitizeNumber } from '../Helper/util'
import { AppCtx } from '@paytrace/pt-skel/Context'
import Persistence from '@paytrace/pt-skel/Context/Persistence'
import { getLegacyDomain } from '@paytrace/pt-skel/Helper'
import {
  renderAmount,
  renderAmountHeader,
  renderSurchargeAmount,
  renderTotalAmount,
} from './TransactionSearchResult'

interface Props extends React.ComponentProps<any> {
  action: string
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => any
}

interface Terminal {
  is_connected: boolean
  label: string
}

interface EmvReportSummary {
  terminal_id: string
  internal_id: string
  terminal: Terminal
}

function getRowLookupValue (
  path: string,
  rowLookups: EmvReportSummary[],
  row: { transaction_id: string },
  defaultValue: any = '',
): string {
  const match = _.find(rowLookups || [], x => x.internal_id === row.transaction_id)

  if (!match) {
    return defaultValue
  }

  return _.get(match, path, defaultValue)
}

function updateTerminalList (rows: { transaction_id: string }[]) {
  const internalIds = rows.map(
    (row: { transaction_id: string }) => row.transaction_id
  )

  PubSub.pub(Event.GET_EMV_REPORT_BY_INTERNAL_IDS, internalIds)
}

function getTerminalDesc (
  rowLookups: EmvReportSummary[],
  row: { transaction_id: string },
): string {
  return getRowLookupValue('terminal.label', rowLookups, row) +
         ' (' +
         getRowLookupValue('terminal.location', rowLookups, row) +
         ') S/N: ' +
         getRowLookupValue('terminal.serial_number', rowLookups, row) +
         ' '
}

// If your text editor doesn't show emojis (like mine), I apologize.
// For whatever reason, it doesn't seem like HTML escape codes will render
// in our <Select> list element, despite showing as literals in the HTML View-Source
const greenCircle = '🟢'
const redCircle = '🔴'

export function SemiIntegratedTransactionSearchResult (props: Props) {
  const context = useContext(AppCtx)
  const [manualAmounts, setManualAmounts] = useState<{ [key: string]: string }>({})
  const [hideRows, setHideRows] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [hasSurcharge, setHasSurcharge] = useState(false)
  const [rowLookups, setRowLookups] = useState<EmvReportSummary[]>([])

  // Load up the terminal details
  useEffect(() => {
    updateTerminalList(props.rows)
  }, [props.rows])

  useEffect(() => {
    if (props.action !== 'refund') {
      return
    }
    const surchargeApplied = props.rows.some(
      (row: { surcharge_was_applied: boolean }) => row.surcharge_was_applied
    )
    setHasSurcharge(surchargeApplied)
  }, [props.rows, props.action])

  useEffect(() => {
    PubSub.sub(self, Event.GET_EMV_REPORT_BY_INTERNAL_IDS_RESPONSE, (rawJson: any) => {
      setRowLookups(rawJson)
    })

    PubSub.sub(self, Event.BATCH_TRANSACTIONS_OP_RESPONSE, (rawJson: any) => {
      window.scrollTo(0, 0)
      setIsLoading(false)

      // Some unexpected error was hit, ugh - this should never be the case, so
      // dump as much info as we can.
      if ('err' in rawJson) {
        const msg = 'An unexpected error was encountered, full info: ' + JSON.stringify(rawJson.err)
        PubSub.pub(Event.NETWORK_ERROR, msg)
        setHideRows(false)

        return
      }

      setHideRows(true)
      // If we got here, we got the expected type of response
      const json: BatchTransactionOpResult[] = rawJson
      const good: BatchTransactionOpResult[] = []
      const bad: BatchTransactionOpResult[] = []

      // For the response_codes:
      // http://localhost:8100/redoc#tag/Response-Codes
      // 106 = successful refund
      // 107 = unsuccessful refund
      // 108 = successful test refund
      // 109 = successful void
      // 110 = unsuccessful void
      // 112 = successful capture
      // 113 = unsuccessful capture
      // Might be safer to just look at the approval_message which should be APPROVAL
      const successCodes = [
        106,
        108,
        109,
        112,
      ]

      const successStatuses = [200, 201] // TODO: Make TSYS MS send a 200 back

      json.forEach((response: BatchTransactionOpResult) => {
        let responseCode = 0

        if (Array.isArray(response.data)) {
          responseCode = response.data[0].response_code
        } else {
          responseCode = response.data.response_code
        }

        if (successStatuses.includes(response.status_code) &&
            successCodes.includes(responseCode)) {
          good.push(response)
        } else {
          bad.push(response)
        }
      })

      const records: { [key: string]: any } = {}

      props.rows.forEach((row: {
        requested_amount: number,
        transaction_id: string }) => {
        const idx = String(row.transaction_id)

        // Ensure our requested_amount reflects the user specified value
        // This is used for feedback in BatchTransactionFeedback only
        if (manualAmounts[idx] !== undefined) {
          row.requested_amount = Number(manualAmounts[idx])
        }

        records[idx] = row
      })

      PubSub.pub(Event.BATCH_TRANSACTION_ERROR_FEEDBACK, { response: bad, records })
      PubSub.pub(Event.BATCH_TRANSACTION_FEEDBACK, { response: good, records })
    })

    return () => {
      PubSub.unsub(self)
    }
  }, [props.rows, manualAmounts])

  function handleManualAmount (
    row: { transaction_id: string }
  ) {
    return (e: any) => {
      setManualAmounts({ ...manualAmounts, [row.transaction_id]: sanitizeNumber(e.target.value) })
    }
  }

  function handleSingleAction (
    transactionId: string,
    terminalId: string,
    terminalNickname: string,
    terminalLabel: string,
    terminalSerialNumber: string,
    amount: string,
  ) {
    const confirmed = confirm(
      `Are you sure you want to ${props.action} the following:

   transaction_id: ${transactionId})
   on terminal: ${terminalNickname}

please note that the terminal must be online and not busy - it will momentarily perform a broadpos event that looks like a transaction.
`
    )

    if (!confirmed) {
      console.debug('Confirm cancel')
      return
    }

    // Dynamic event on the SemiIntegratedCard page
    const check = String(Math.random())
    Persistence.setItem(
      'semi-integrated-auto-action',
      {
        transactionId,
        terminalId,
        terminalLabel,
        terminalSerialNumber,
        amount,
        check,
      }
    )

    window.location.href = `/semiintegrated.pay?auto-action=void&check=${check}`
  }

  function renderTable (rows: any[]) {
    const domain = getLegacyDomain() + '/view.pay?b='
    const view_link = (id: string) => domain + id
    return (
      <table>
        <thead>
          <tr>
            <th>&nbsp;</th>
            <th>Order ID</th>
            <th>Card Number</th>
            <th>{renderAmountHeader(props.action)}</th>
            {hasSurcharge &&
            <>
              <th>Surcharge Refund</th>
              <th>Total Refund</th>
            </>
            }
            <th>Date/Time</th>
            <th>Invoice</th>
            <th>Name</th>
            <th>AVS</th>
            <th>CSC</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, iter) => {
            const isConnected = getRowLookupValue(
              'terminal.is_connected',
              rowLookups,
              row,
              false
            )
            const terminalIcon = isConnected ? greenCircle : redCircle
            const buttonStyle: any = isConnected ? {} : { opacity: '0.3' }
            const userHasPermission = hasPermission({
              records: context.records,
              required_permissions: [props.action],
            })

            const terminalDesc = getTerminalDesc(rowLookups, row)
            // So much for a one size fit's all solution.  This page is used
            // for void/refund/auth list items, however they each have a special
            // behavior.
            //   Refunds: Using max_refundable_amount, there is a refund column, and
            //     this should set the 'amount' value alongside each record in the /batch
            //     sent data.  This also may need 2 more surcharge refund columns when
            //     we incorporate that work.
            //   Voids: Well, no special behavior
            //   Capture: The 'amount' should be user supplied, but I assume, not exceed
            //     requested_amount (I am not sure if this is enforced).  This is to
            //     support partial capture.  I don't think a partial capture can capture
            //     multiple times.

            // TODO: Also need the customer id for linking to something like:
            // https://vt.devqa.paytrace.com/mcust.pay?a=mcust&cid=11268074
            return (
              <React.Fragment key={iter}>
              <tr>
                <td>
                  <If is={userHasPermission}>
                    <button
                      disabled={!isConnected}
                      style={buttonStyle}
                      type='button'
                      onClick={() => handleSingleAction(
                        row.transaction_id,
                        getRowLookupValue('terminal.terminal_id', rowLookups, row),
                        terminalDesc,
                        getRowLookupValue('terminal.label', rowLookups, row),
                        getRowLookupValue('terminal.serial_number', rowLookups, row),
                        row.settle_amount,
                      )}>
                      Void
                    </button>
                  </If>
                </td>
                <td>
                  <a href={view_link(row.transaction_id)}
                     rel='noreferrer'
                     target='_blank'>
                    {row.transaction_id}
                  </a>
                </td>
                <td>{row.card_masked}</td>
                <td>
                  {renderAmount(
                    props.action,
                    row,
                    handleManualAmount(row),
                    manualAmounts)}
                </td>
                {hasSurcharge &&
                <>
                  <td>{renderSurchargeAmount(row, manualAmounts)}</td>
                  <td>{renderTotalAmount(row, manualAmounts)}</td>
                </>
                }
                <td>{new Date(row.created_at).toLocaleString()}</td>
                <td>{row.invoice_number}</td>
                <td>{row.billing_name}</td>
                <td>&nbsp;</td>
                <td>&nbsp;</td>
              </tr>
              <If is={userHasPermission}>
                <tr>
                  <td colSpan={9}>
                    <button
                      type="button"
                      onClick={(_) => updateTerminalList(props.rows)}
                      title='Update list'
                      style={{
                        background: '#fff',
                        color: 'blue',
                        display: 'inline-block',
                        border: '0px',
                        fontSize: '20px',
                        marginRight: '0px',
                        paddingRight: '0px',
                      }}>
                      &#x21BB;
                    </button>
                    &nbsp;&nbsp;&nbsp; {terminalIcon} &nbsp;
                    <em>
                      Requires&nbsp;
                      <strong>{terminalDesc}</strong>
                      to be online to void.
                    </em>
                  </td>
                </tr>
              </If>
              </React.Fragment>
            )
          })}
        </tbody>
      </table>
    )
  }

  if (isLoading) {
    return (
      <Loader />
    )
  }

  if (hideRows) {
    return (
      <React.Fragment />
    )
  }

  return (
    <form onSubmit={(_) => false}>
      <Fieldset>
        <Legend title='Search Results'></Legend>
        <div className='clear' />

        <If is={props.rows.length > 0}>
          {renderTable(props.rows)}
        </If>

        <div className='info-msg msg-space'>
          {props.rows.length} transactions were found in this date range.
        </div>
      </Fieldset>
    </form>
  )
}

export default SemiIntegratedTransactionSearchResult
